/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Styles from './CustomEmblaCarousel.css';
import PaginationBullets from '../PaginationBullets/PaginationBullets';

/**
 *
 * @typedef {Object} CustomEmblaCarouselProps
 * @property {EmblaOptionsType} options
 */

/**
 *
 * @param {CustomEmblaCarouselProps} props
 * @returns {React.JSX.Element}
 */
function CustomEmblaCarousel({
  children,
  fillHeight,
  loop,
  autoplay,
  onEmblaApiReady,
  onSlideChanged,
  hideOverflow,
  useBigArrows,
  enableBullets,
  options,
  enableMobileArrows,
  bulletCount,
  detectBulletCount,
  enablePrevArrow,
  ariaLabelBullet,
  customEmblaCarouselRef,
  ...props
}) {
  const [enablePrev, setEnablePrev] = useState(enablePrevArrow ? false : true);
  const [enableNext, setEnableNext] = useState(true);
  const [snap, setSnap] = useState(0);

  const fillHeightClass = useMemo(() => (fillHeight ? 'fill-height' : ''), [fillHeight]);
  const overflowClass = useMemo(() => (hideOverflow ? 'hide-overflow' : ''), [hideOverflow]);
  const enablePrevClass = useMemo(() => (enablePrev ? '' : 'disabled'), [enablePrev]);
  const enableNextClass = useMemo(() => (enableNext ? '' : 'disabled'), [enableNext]);
  const bigArrowsClass = useMemo(() => (useBigArrows ? 'big-arrow' : ''), [useBigArrows]);
  const mobileArrowsClass = useMemo(
    () => (enableMobileArrows ? 'enable-mobile-arrows' : ''),
    [enableMobileArrows],
  );

  const autoplayRef = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false }, (emblaRoot) => emblaRoot.parentElement),
  );

  const plugins = useMemo(() => {
    if (autoplay) return [autoplayRef.current];
    return [];
  }, [autoplay]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      direction: 'lrf',
      align: 0,
      containScroll: 'trimSnaps',
      speed: 15,
      ...options,
    },
    plugins,
  );

  const bulletsToRender = useMemo(() => {
    if (detectBulletCount && emblaApi) {
      return emblaApi.scrollSnapList().length;
    }
    return bulletCount;
  }, [bulletCount, emblaApi, detectBulletCount]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (autoplay) {
      autoplayRef.current.stop();
    }
  }, [autoplay, emblaApi, autoplayRef]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (autoplay) {
      autoplayRef.current.stop();
    }
  }, [autoplay, emblaApi, autoplayRef]);

  const updateButtons = useCallback(() => {
    if (!emblaApi) return;
    setSnap(emblaApi.selectedScrollSnap());
    setEnableNext(emblaApi.canScrollNext());
    setEnablePrev(emblaApi.canScrollPrev);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('settle', updateButtons);
    emblaApi.on('scroll', updateButtons);
    emblaApi.on('init', updateButtons);
    emblaApi.on('settle', (evt) => {
      if (onSlideChanged) {
        onSlideChanged(emblaApi.internalEngine().index.get());
      }
    });
  }, [emblaApi, onSlideChanged, updateButtons]);

  useEffect(() => {
    if (customEmblaCarouselRef) {
      customEmblaCarouselRef.current = emblaApi;
    }
  }, [customEmblaCarouselRef, emblaApi]);

  return (
    <>
      <div
        {...props}
        className={['embla', 'custom-carousel', fillHeightClass, overflowClass].join(' ')}
      >
        <div className={['embla__viewport', fillHeightClass].join(' ')} ref={emblaRef}>
          <div className={['embla__container', fillHeightClass].join(' ')}>
            {children({ className: 'embla__slide' })}
          </div>
          <button
            className={['embla__prev', enablePrevClass, bigArrowsClass, mobileArrowsClass].join(
              ' ',
            )}
            aria-label="Banner anterior"
            role="button"
            onClick={scrollPrev}
          />
          <button
            className={['embla__next', enableNextClass, bigArrowsClass, mobileArrowsClass].join(
              ' ',
            )}
            aria-label="Próximo banner"
            role="button"
            onClick={scrollNext}
          />
        </div>
      </div>
      {enableBullets && (
        <section className="home-pagination">
          <PaginationBullets
            value={bulletsToRender}
            id="homeCarouselPagination"
            carouselId="homeCarouselBanner"
            activeIndex={snap}
            onBulletClick={(index) => {
              emblaApi.scrollTo(index);
              if (autoplay) {
                autoplayRef.current.reset();
              }
            }}
            ariaLabelBullet={ariaLabelBullet}
          />
        </section>
      )}
      <style jsx>{Styles}</style>
    </>
  );
}

CustomEmblaCarousel.defaultProps = {
  children: () => null,
  fillHeight: true,
  loop: false,
  autoplay: false,
  onEmblaApiReady: () => {},
  onSlideChanged: () => {},
  hideOverflow: false,
  useBigArrows: false,
  enableBullets: false,
  options: {},
  enableMobileArrows: false,
  bulletCount: 5,
  ariaLabelBullet: [],
};

export default CustomEmblaCarousel;
